import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getFactoryQueue,
  updateOrderStatus,
  getWorkersByFactory,
  getFactories,
  startOrderPart,
  setItemReady,
  moveItemFactory,
  ackCancelledPart,
} from '../services/api';
import { imageUrl } from '../utils/image';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import toast from 'react-hot-toast';

// A card here is one factory's SHARE of an order, not the whole order.
// Its state is worked out from that share alone:
//   nothing started           → Νέα
//   started, not all ticked   → Σε εξέλιξη
//   every item ticked         → Έτοιμα
const COLS = ['NEW', 'IN_PROGRESS', 'READY'];
const COL_LABELS = { NEW: 'Νέα', IN_PROGRESS: 'Σε εξέλιξη', READY: 'Έτοιμα' };
const COL_ICON = { NEW: '🆕', IN_PROGRESS: '⚙️', READY: '✅' };
const COL_COLOR = {
  NEW: { bg: 'var(--blue-light)', text: 'var(--blue-dark)' },
  IN_PROGRESS: { bg: '#fef3c7', text: '#92400e' },
  READY: { bg: '#dcfce7', text: 'var(--green-dark)' },
};

const POLL_MS = 12000;

const normalize = (str) => (str || '').toString().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '');

const colOf = (o) => {
  const all = o.my_item_count > 0 && o.my_ready_count === o.my_item_count;
  if (all) return 'READY';
  if (o.my_started_at || o.my_ready_count > 0) return 'IN_PROGRESS';
  return 'NEW';
};

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const beep = (freq, when, duration = 0.15) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.value = 0.0;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + when);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + when + 0.02);
      gain.gain.linearRampToValueAtTime(0.0, ctx.currentTime + when + duration);
      osc.stop(ctx.currentTime + when + duration + 0.02);
    };
    beep(880, 0);
    beep(1320, 0.18);
    setTimeout(() => { try { ctx.close(); } catch (e) {} }, 500);
  } catch (e) { /* browser audio policy — ignore */ }
};

export default function FactoryQueue() {
  const { selectedFactory } = useAuth();
  const [orders, setOrders] = useState([]);
  const [done, setDone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});          // { [key]: true }
  const [search, setSearch] = useState('');
  const [showDone, setShowDone] = useState(false);
  // Pickup-date filter. Defaults to 'all' — a baker should see every pending
  // job unless they deliberately narrow it.
  const [dateRange, setDateRange] = useState('all'); // today|yesterday|last7|month|all|custom
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [isWide, setIsWide] = useState(window.innerWidth >= 900);
  const [workers, setWorkers] = useState([]);
  const [factories, setFactories] = useState([]);

  // Modals
  const [startFor, setStartFor] = useState(null);      // order awaiting Ξεκινώ
  const [startWorker, setStartWorker] = useState('');
  const [pendingTick, setPendingTick] = useState(null); // item to tick after starting
  const [moveFor, setMoveFor] = useState(null);         // { order, item }
  const [moveTarget, setMoveTarget] = useState('');

  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem('orders_soundOn') === '1'; } catch (e) { return false; }
  });

  const inFlightRef = useRef(false);
  const prevIdsRef = useRef(new Set());
  // Cancellations already announced, so the bell rings once per order
  const prevCancelIdsRef = useRef(new Set());

  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = useCallback(async (silent = false) => {
    if (inFlightRef.current || !selectedFactory) return;
    inFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const [activeRes, doneRes] = await Promise.all([
        getFactoryQueue(selectedFactory.id, false),
        getFactoryQueue(selectedFactory.id, true),
      ]);
      const active = activeRes.data || [];

      // New arrivals = orders we hadn't seen that aren't started yet
      const ids = new Set(
        active.filter((o) => o.status !== 'CANCELLED' && colOf(o) === 'NEW').map((o) => o.id)
      );
      if (silent && prevIdsRef.current.size > 0 && soundOn) {
        const fresh = [...ids].filter((id) => !prevIdsRef.current.has(id));
        if (fresh.length > 0) {
          playBeep();
          if ('Notification' in window && Notification.permission === 'granted') {
            try {
              const arrived = active.filter((o) => fresh.includes(o.id));
              new Notification(
                fresh.length === 1 ? `Νέα παραγγελία #${arrived[0].id}` : `${fresh.length} νέες παραγγελίες`,
                { body: arrived.map((o) => o.customer_name).join('\n'), tag: 'orders-app-new' }
              );
            } catch (e) { /* ignore */ }
          }
        }
      }
      prevIdsRef.current = ids;

      // A cancellation only rings when this factory had already put work in.
      // One that arrives before anyone touched the order is on the board but
      // does not interrupt the room.
      const ringing = new Set(
        active
          .filter((o) => o.status === 'CANCELLED' && (o.my_started_at || o.my_ready_count > 0))
          .map((o) => o.id)
      );
      if (silent && soundOn) {
        const freshCancels = [...ringing].filter((id) => !prevCancelIdsRef.current.has(id));
        if (freshCancels.length > 0) {
          playBeep();
          const which = active.filter((o) => freshCancels.includes(o.id));
          toast.error(
            freshCancels.length === 1
              ? `Ακυρώθηκε η παραγγελία #${which[0].id}${which[0].customer_name ? ' — ' + which[0].customer_name : ''}`
              : `Ακυρώθηκαν ${freshCancels.length} παραγγελίες που είχατε ξεκινήσει`,
            { duration: 8000 }
          );
          if ('Notification' in window && Notification.permission === 'granted') {
            try {
              new Notification(
                freshCancels.length === 1 ? `Ακυρώθηκε η #${which[0].id}` : `${freshCancels.length} ακυρώσεις`,
                { body: which.map((o) => o.customer_name).join('\n'), tag: 'orders-app-cancel' }
              );
            } catch (e) { /* ignore */ }
          }
        }
      }
      prevCancelIdsRef.current = ringing;

      setOrders(active);
      setDone(doneRes.data || []);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης');
    } finally {
      if (!silent) setLoading(false);
      inFlightRef.current = false;
    }
  }, [selectedFactory, soundOn]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!selectedFactory) { setWorkers([]); return; }
    getWorkersByFactory(selectedFactory.id).then((r) => setWorkers(r.data)).catch(() => setWorkers([]));
  }, [selectedFactory]);

  useEffect(() => {
    getFactories().then((r) => setFactories(r.data)).catch(() => setFactories([]));
  }, []);

  const mark = (key, on) => setBusy((b) => {
    const n = { ...b };
    if (on) n[key] = true; else delete n[key];
    return n;
  });

  // ── Ξεκινώ ────────────────────────────────────────────────
  const askStart = (order, itemToTickAfter = null) => {
    setStartFor(order);
    setStartWorker('');
    setPendingTick(itemToTickAfter);
  };

  const confirmStart = async () => {
    if (!startFor || !startWorker) return toast.error('Επιλέξτε εργαζόμενο');
    const order = startFor;
    const item = pendingTick;
    mark(`o${order.id}`, true);
    try {
      await startOrderPart(order.id, selectedFactory.id, Number(startWorker));
      // If the baker went straight for a tick, apply it now
      if (item) await setItemReady(order.id, item.id, true, Number(startWorker));
      setStartFor(null);
      setPendingTick(null);
      setStartWorker('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
      load();
    } finally {
      mark(`o${order.id}`, false);
    }
  };

  // ── Tick a product ────────────────────────────────────────
  const toggleItem = async (order, item) => {
    const makeReady = item.status !== 'READY';
    // Ticking before anyone pressed Ξεκινώ → ask who's taking it first
    if (makeReady && !order.my_started_at) return askStart(order, item);

    mark(`i${item.id}`, true);
    try {
      await setItemReady(order.id, item.id, makeReady, order.my_started_by_worker_id || null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
      load();
    } finally {
      mark(`i${item.id}`, false);
    }
  };

  // ── Move a product to another factory ─────────────────────
  const confirmMove = async () => {
    if (!moveFor || !moveTarget) return toast.error('Επιλέξτε εργοστάσιο');
    const { order, item } = moveFor;
    mark(`i${item.id}`, true);
    try {
      const r = await moveItemFactory(order.id, item.id, Number(moveTarget));
      toast.success(`${item.product_name} → ${r.data.to_factory}`);
      setMoveFor(null);
      setMoveTarget('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      mark(`i${item.id}`, false);
    }
  };

  // "Κατάλαβα" — clears the cancellation from this factory's board only.
  const ackCancel = async (order) => {
    setBusy((b) => ({ ...b, [`c${order.id}`]: true }));
    try {
      await ackCancelledPart(order.id, selectedFactory.id);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setBusy((b) => ({ ...b, [`c${order.id}`]: false }));
    }
  };

  const cancelOrder = async (order) => {
    if (!window.confirm(`Ακύρωση ΟΛΟΚΛΗΡΗΣ της παραγγελίας #${order.id};${order.factory_count > 1 ? '\n\nΠροσοχή: η παραγγελία αφορά και άλλο εργοστάσιο.' : ''}`)) return;
    mark(`o${order.id}`, true);
    try {
      await updateOrderStatus(order.id, 'CANCELLED', null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      mark(`o${order.id}`, false);
    }
  };

  const toggleSound = async () => {
    const v = !soundOn;
    setSoundOn(v);
    try { localStorage.setItem('orders_soundOn', v ? '1' : '0'); } catch (e) {}
    if (v) {
      if ('Notification' in window && Notification.permission === 'default') {
        try { await Notification.requestPermission(); } catch (e) {}
      }
      playBeep();
      toast.success('Ηχητικές ειδοποιήσεις ενεργές');
    } else {
      toast.success('Ηχητικές ειδοποιήσεις απενεργοποιημένες');
    }
  };

  const fmtFull = (iso) => format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: el });
  const fmtShort = (iso) => format(new Date(iso), 'd MMM, HH:mm', { locale: el });

  // Resolve the chosen preset to a [from, to] pair of yyyy-MM-dd strings.
  // Both ends inclusive; null means unbounded.
  const dayStr = (d) => format(d, 'yyyy-MM-dd');
  const resolveRange = () => {
    const now = new Date();
    if (dateRange === 'all') return [null, null];
    if (dateRange === 'custom') return [customFrom || null, customTo || null];
    if (dateRange === 'today') return [dayStr(now), dayStr(now)];
    if (dateRange === 'yesterday') {
      const d = new Date(now); d.setDate(d.getDate() - 1);
      return [dayStr(d), dayStr(d)];
    }
    if (dateRange === 'last7') {
      const d = new Date(now); d.setDate(d.getDate() - 6);
      return [dayStr(d), dayStr(now)];
    }
    if (dateRange === 'month') {
      return [dayStr(new Date(now.getFullYear(), now.getMonth(), 1)), dayStr(now)];
    }
    return [null, null];
  };
  const [rangeFrom, rangeTo] = resolveRange();

  // Compares on the PICKUP date — what a baker means by "show me Saturday".
  // requested_for arrives as an instant, so it's rendered to a local
  // yyyy-MM-dd before comparing against the bounds.
  const inDateRange = (o) => {
    if (!rangeFrom && !rangeTo) return true;
    if (!o.requested_for) return false;
    const day = format(new Date(o.requested_for), 'yyyy-MM-dd');
    if (rangeFrom && day < rangeFrom) return false;
    if (rangeTo && day > rangeTo) return false;
    return true;
  };

  const match = (o) => {
    if (!inDateRange(o)) return false;
    if (!search.trim()) return true;
    const q = normalize(search);
    return normalize(o.customer_name).includes(q) || String(o.id).includes(q);
  };

  const dateFilterOn = dateRange !== 'all';
  const visible = orders.filter(match);
  // Cancelled orders are an alert, not a stage of the work, so they are
  // pulled out of the columns and shown above them until acknowledged.
  const cancelled = orders.filter((o) => o.status === 'CANCELLED');
  const working = visible.filter((o) => o.status !== 'CANCELLED');
  const grouped = {
    NEW: working.filter((o) => colOf(o) === 'NEW'),
    IN_PROGRESS: working.filter((o) => colOf(o) === 'IN_PROGRESS'),
    READY: working.filter((o) => colOf(o) === 'READY'),
  };

  // ───────────────────────── Card ─────────────────────────
  const Card = ({ o }) => {
    const col = colOf(o);
    const color = COL_COLOR[col];
    const oBusy = !!busy[`o${o.id}`];
    // Everything of ours left this order. The card stays on the board as a
    // record — muted, but at full contrast so it is still easy to read.
    const released = !!o.my_released_at;
    const movedTo = (o.items || [])
      .filter((it) => it.moved_away && it.moved_to_factory_name)
      .map((it) => it.moved_to_factory_name);
    const movedToName = movedTo.length > 0 ? movedTo[0] : null;
    return (
      <div style={{
        background: released ? 'var(--gray-50)' : 'white',
        border: '1px solid var(--gray-200)',
        borderLeft: `4px solid ${released ? 'var(--gray-300)' : color.text}`,
        borderRadius: 8,
        marginBottom: 8,
      }}>
        <div style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700 }}>#{o.id}</span>
            <span style={{ fontWeight: 500 }}>{o.customer_name || '(χωρίς όνομα)'}</span>
            {o.customer_phone && <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>📞 {o.customer_phone}</span>}
            {o.factory_count > 1 && (
              <span className="badge badge-blue" style={{ fontSize: 10 }}>
                + {o.factory_count - 1} ακόμη εργοστάσιο
              </span>
            )}
            {o.edit_count > 0 && (
              <span className="badge badge-amber" style={{ fontSize: 10 }}>✏️ Τροποποιήθηκε</span>
            )}
            {released && (
              <span className="badge badge-gray" style={{ fontSize: 10 }}>
                ➡️ Μεταφέρθηκε{movedToName ? ` στο ${movedToName}` : ''}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
            🏪 {o.shop_name}
            {o.created_by_worker_name && <> · από {o.created_by_worker_name}</>}
            {o.my_started_by_worker_name && <> · ⚙️ ανέλαβε {o.my_started_by_worker_name}</>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
            📅 Παραλαβή: <strong>{fmtFull(o.requested_for)}</strong>
            {!released && <>
              {' · '}€{parseFloat(o.my_total).toFixed(2)}
              {' · '}<strong style={{ color: o.my_ready_count === o.my_item_count ? 'var(--green-dark)' : 'inherit' }}>
                {o.my_ready_count}/{o.my_item_count} έτοιμα
              </strong>
            </>}
          </div>

          {o.customer_notes && (
            <div style={{ marginTop: 8, padding: '7px 10px', background: '#fef3c7', borderRadius: 6, fontSize: 12 }}>
              📝 {o.customer_notes}
            </div>
          )}

          {/* The shop's reference photos — tap to open full size */}
          {(o.images || []).length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {o.images.map((img) => (
                <a key={img.id} href={imageUrl(img.access_key)} target="_blank" rel="noreferrer">
                  <img
                    src={imageUrl(img.access_key)}
                    alt=""
                    style={{
                      width: 64, height: 64, objectFit: 'cover',
                      borderRadius: 6, border: '1px solid var(--gray-200)',
                      display: 'block', background: 'white',
                    }}
                  />
                </a>
              ))}
            </div>
          )}

          {/* The products — ticking these IS the work */}
          <div style={{ marginTop: 10, borderTop: '1px solid var(--gray-100)', paddingTop: 8 }}>
            {o.items.map((it) => {
              const isReady = it.status === 'READY';
              const iBusy = !!busy[`i${it.id}`];
              // Handed to another factory: kept on the list so nobody wonders
              // where it went, but with nothing to tick and nothing to press.
              const gone = !!it.moved_away;
              return (
                <div key={it.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '7px 0', borderBottom: '1px solid var(--gray-100)',
                  background: gone ? 'var(--gray-50)' : 'transparent',
                }}>
                  {gone ? (
                    <span style={{ width: 20, textAlign: 'center', flexShrink: 0, fontSize: 13 }} title="Μεταφέρθηκε">➡️</span>
                  ) : (
                    <input
                      type="checkbox"
                      checked={isReady}
                      disabled={iBusy || oBusy}
                      onChange={() => toggleItem(o, it)}
                      style={{ width: 20, height: 20, cursor: 'pointer', flexShrink: 0 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 500,
                      textDecoration: isReady && !gone ? 'line-through' : 'none',
                      color: gone ? 'var(--gray-600)' : (isReady ? 'var(--gray-400)' : 'var(--gray-900)'),
                    }}>
                      {it.product_name} × {parseFloat(it.quantity).toLocaleString('el-GR', { maximumFractionDigits: 3 })}
                    </div>
                    {gone && (
                      <div style={{ fontSize: 11, color: '#92400e' }}>
                        ➡️ Μεταφέρθηκε{it.moved_to_factory_name ? ` στο ${it.moved_to_factory_name}` : ''}
                      </div>
                    )}
                    {it.notes && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>📝 {it.notes}</div>}
                    {isReady && !gone && it.ready_by_worker_name && (
                      <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>✓ {it.ready_by_worker_name}</div>
                    )}
                  </div>
                  {!isReady && !gone && (
                    <button
                      className="btn btn-secondary btn-sm"
                      title="Μεταφορά σε άλλο εργοστάσιο"
                      onClick={() => { setMoveFor({ order: o, item: it }); setMoveTarget(''); }}
                      disabled={iBusy}
                      style={{ padding: '2px 8px' }}
                    >➡️</button>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {!released && !o.my_started_at && (
              <button className="btn btn-primary btn-sm" disabled={oBusy} onClick={() => askStart(o)}>
                ▶ Ξεκινώ
              </button>
            )}
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => window.open(`/print/${o.id}?factory=${selectedFactory.id}`, '_blank')}
            >🖨</button>
            {!released && (
              <button className="btn btn-danger btn-sm" disabled={oBusy} onClick={() => cancelOrder(o)}>
                Ακύρωση
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const Header = ({ status, count }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px',
      background: COL_COLOR[status].bg, color: COL_COLOR[status].text,
      borderRadius: 8, fontWeight: 600, fontSize: 13, marginBottom: 10,
    }}>
      <span style={{ fontSize: 16 }}>{COL_ICON[status]}</span>
      <span>{COL_LABELS[status]}</span>
      <span style={{ marginLeft: 'auto', background: 'white', borderRadius: 999, padding: '1px 9px', fontSize: 12 }}>
        {count}
      </span>
    </div>
  );

  if (!selectedFactory) {
    return (
      <>
        <div className="page-header"><h2>🏭 Παραγγελίες Εργοστασίου</h2></div>
        <div className="page-body">
          <div className="card"><div className="card-body">
            <div className="empty-state">
              <div>🏭</div>
              <p>Δεν έχετε επιλεγμένο εργοστάσιο. Επικοινωνήστε με τον διαχειριστή.</p>
            </div>
          </div></div>
        </div>
      </>
    );
  }

  const totalActive = working.length;

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>🏭 Παραγγελίες Εργοστασίου</h2>
            <p>Εργοστάσιο: <strong>{selectedFactory.name}</strong> · {totalActive} ενεργές</p>
          </div>
          <div className="flex-row">
            <input
              className="form-input"
              placeholder="🔍 #ID ή πελάτης..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 200, padding: '6px 10px', margin: 0 }}
            />
            <select
              className="form-select"
              value={dateRange}
              onChange={(e) => {
                const v = e.target.value;
                setDateRange(v);
                if (v === 'custom' && !customFrom && !customTo) {
                  const t = format(new Date(), 'yyyy-MM-dd');
                  setCustomFrom(t);
                  setCustomTo(t);
                }
              }}
              title="Φίλτρο ημερομηνίας παραλαβής"
              style={{ width: 170, padding: '6px 10px' }}
            >
              <option value="all">📅 Όλες</option>
              <option value="today">Σήμερα</option>
              <option value="yesterday">Χθες</option>
              <option value="last7">Τελευταίες 7</option>
              <option value="month">Αυτόν τον μήνα</option>
              <option value="custom">Επιλογή ημερομηνιών…</option>
            </select>
            <button className="btn btn-secondary" onClick={toggleSound}>{soundOn ? '🔔' : '🔕'}</button>
            <button className="btn btn-secondary" onClick={() => load()}>↻</button>
          </div>
        </div>

        {dateRange === 'custom' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            marginTop: 10, padding: '10px 12px',
            background: 'var(--blue-light, #eff6ff)', border: '1px solid #bfdbfe', borderRadius: 8,
          }}>
            <span style={{ fontSize: 13, color: '#1e40af', fontWeight: 600 }}>📅 Παραλαβή από</span>
            <input
              type="date"
              className="form-input"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
              style={{ width: 160, padding: '5px 8px', margin: 0 }}
            />
            <span style={{ fontSize: 13, color: '#1e40af', fontWeight: 600 }}>έως</span>
            <input
              type="date"
              className="form-input"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
              style={{ width: 160, padding: '5px 8px', margin: 0 }}
            />
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const t = format(new Date(), 'yyyy-MM-dd');
                setCustomFrom(t); setCustomTo(t);
              }}
            >Σήμερα</button>
          </div>
        )}
      </div>

      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : null}

        {/* Cancellations. Shown whatever the date filter says, because the
            point is that nobody keeps working on a cancelled order. Each
            factory clears its own; a colleague's tap removes it here too on
            the next poll. */}
        {!loading && cancelled.length > 0 && (
          <div style={{
            marginBottom: 14, borderRadius: 10,
            border: '1px solid #fca5a5', background: '#fef2f2',
            padding: '12px 14px',
          }}>
            <div style={{ fontWeight: 700, color: 'var(--red-dark)', marginBottom: 8, fontSize: 14 }}>
              ❌ {cancelled.length === 1 ? 'Ακυρώθηκε παραγγελία' : `Ακυρώθηκαν ${cancelled.length} παραγγελίες`}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cancelled.map((o) => {
                const started = !!o.my_started_at || o.my_ready_count > 0;
                return (
                  <div key={o.id} style={{
                    background: 'white', border: '1px solid #fecaca', borderRadius: 8,
                    padding: '10px 12px',
                    display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
                  }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontWeight: 600, color: 'var(--gray-900)' }}>
                        #{o.id} {o.customer_name || '(χωρίς όνομα)'}
                        {started && (
                          <span className="badge badge-red" style={{ marginLeft: 8, fontSize: 10 }}>
                            είχε ξεκινήσει
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 3 }}>
                        🏪 {o.shop_name}
                        {' · '}📅 Παραλαβή: {fmtFull(o.requested_for)}
                        {o.my_item_count > 0 && <> · {o.my_ready_count}/{o.my_item_count} έτοιμα</>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                        {(o.items || []).map((it) => `${it.product_name} × ${parseFloat(it.quantity)}`).join(' · ')}
                      </div>
                      {o.cancelled_by_name && (
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                          Ακυρώθηκε από {o.cancelled_by_name}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => window.open(`/print/${o.id}?factory=${selectedFactory.id}`, '_blank')}
                      >🖨</button>
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={!!busy[`c${o.id}`]}
                        onClick={() => ackCancel(o)}
                      >✓ Κατάλαβα</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? null : totalActive === 0 && cancelled.length === 0 ? (
          <div className="card"><div className="card-body">
            <div className="empty-state">
              <div>📭</div>
              <p>{orders.length === 0
                ? 'Δεν υπάρχουν ενεργές παραγγελίες'
                : dateFilterOn
                  ? 'Καμία παραγγελία σε αυτές τις ημερομηνίες'
                  : 'Καμία παραγγελία δεν ταιριάζει με την αναζήτηση'}</p>
            </div>
          </div></div>
        ) : isWide ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            {COLS.map((c) => (
              <div key={c}>
                <Header status={c} count={grouped[c].length} />
                {grouped[c].length === 0 ? (
                  <div style={{ padding: 12, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13, background: 'white', border: '1px dashed var(--gray-200)', borderRadius: 8 }}>—</div>
                ) : grouped[c].map((o) => <Card key={o.id} o={o} />)}
              </div>
            ))}
          </div>
        ) : (
          <div>
            {COLS.map((c) => grouped[c].length > 0 && (
              <div key={c} style={{ marginBottom: 16 }}>
                <Header status={c} count={grouped[c].length} />
                {grouped[c].map((o) => <Card key={o.id} o={o} />)}
              </div>
            ))}
          </div>
        )}

        {done.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div
              onClick={() => setShowDone((s) => !s)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                background: 'var(--gray-100)', color: 'var(--gray-700)',
                borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <span>📦</span>
              <span>Ολοκληρωμένες σήμερα</span>
              <span style={{ background: 'white', borderRadius: 999, padding: '1px 9px', fontSize: 12 }}>{done.length}</span>
              <span style={{ marginLeft: 'auto' }}>{showDone ? '▴' : '▾'}</span>
            </div>
            {showDone && (
              <div style={{ marginTop: 10 }}>
                {done.filter(match).map((o) => (
                  <div key={o.id} style={{
                    padding: '10px 14px', background: 'white', border: '1px solid var(--gray-100)',
                    borderRadius: 8, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, opacity: 0.75,
                  }}>
                    <span style={{ fontSize: 18 }}>{o.status === 'CANCELLED' ? '❌' : '📦'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>#{o.id} {o.customer_name || '(χωρίς όνομα)'}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        🏪 {o.shop_name} · {o.status === 'CANCELLED' ? 'Ακυρώθηκε' : 'Παραδόθηκε'} · {fmtShort(o.last_status_change_at)}
                      </div>
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--gray-600)' }}>€{parseFloat(o.my_total).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Ξεκινώ — who's taking it */}
      {startFor && (
        <div onClick={() => { setStartFor(null); setPendingTick(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 420 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', fontWeight: 600 }}>
              Έναρξη παραγγελίας #{startFor.id}
            </div>
            <div style={{ padding: 20 }}>
              {pendingTick && (
                <div style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 12 }}>
                  Θα σημειωθεί και το «{pendingTick.product_name}» ως έτοιμο.
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Ποιος αναλαμβάνει; *</label>
                <select className="form-select" value={startWorker} onChange={(e) => setStartWorker(e.target.value)} autoFocus>
                  <option value="">-- Επιλέξτε --</option>
                  {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => { setStartFor(null); setPendingTick(null); }}>Άκυρο</button>
              <button className="btn btn-primary" onClick={confirmStart} disabled={!startWorker}>▶ Έναρξη</button>
            </div>
          </div>
        </div>
      )}

      {/* Move one product to another factory */}
      {moveFor && (
        <div onClick={() => setMoveFor(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 460 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', fontWeight: 600 }}>
              Μεταφορά προϊόντος σε άλλο εργοστάσιο
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 14, marginBottom: 12 }}>
                <strong>{moveFor.item.product_name}</strong> (παραγγελία #{moveFor.order.id})
              </div>
              <div className="form-group">
                <label className="form-label">Σε ποιο εργοστάσιο; *</label>
                <select className="form-select" value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} autoFocus>
                  <option value="">-- Επιλέξτε --</option>
                  {factories.filter((f) => f.id !== selectedFactory.id).map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px' }}>
                ⚠️ Μεταφέρεται μόνο αυτή η παραγγελία. Αν το προϊόν είναι μόνιμα καταχωρημένο σε λάθος
                εργοστάσιο, πρέπει να διορθωθεί από τον διαχειριστή στα Προϊόντα — αλλιώς θα ξανασυμβεί.
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setMoveFor(null)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={confirmMove} disabled={!moveTarget}>➡️ Μεταφορά</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
